/**
 * OneAdvanced API reference and interface.
 *
 * The interface is namespaced by module so adding a new capability
 * does not conflict with existing method names.
 *
 * Usage:
 *   const api = useApi(oneadvancedApiRef);
 *   const categories = await api.layouts.getCategories('workflows');
 */

import { createApiRef } from '@backstage/core-plugin-api';
import type {
  ContentType,
  CreateCategoryInput,
  CreateSectionInput,
  LayoutCategory,
  LayoutItem,
  LayoutSection,
  ReorderItem,
  UpdateCategoryInput,
  UpdateSectionInput,
} from '../modules/layouts/types';
import type {
  Banner,
  CreateBannerInput,
  UpdateBannerInput,
} from '../modules/banners/types';
import type { ToolchainApproval, HarnessExecutionStatus } from '../modules/pending-approvals/types';
import type {
  FaqSection,
  FaqCategory,
  FaqEntry,
  FeedbackSummary,
  FeedbackVoters,
  ReorderFaqItem,
  CreateFaqSectionInput,
  UpdateFaqSectionInput,
  CreateFaqCategoryInput,
  UpdateFaqCategoryInput,
  CreateFaqEntryInput,
  UpdateFaqEntryInput,
} from '../modules/faq/types';

// ---------------------------------------------------------------------------
// API reference
// ---------------------------------------------------------------------------

export const oneadvancedApiRef = createApiRef<OneAdvancedApi>({
  id: 'plugin.oneadvanced',
});

// ---------------------------------------------------------------------------
// Interface — namespaced by module
// ---------------------------------------------------------------------------

export interface OneAdvancedApi {
  layouts: {
    // ── Sections ───────────────────────────────────────────────────────────
    getSections(contentType: ContentType): Promise<LayoutSection[]>;
    createSection(
      contentType: ContentType,
      data: CreateSectionInput,
    ): Promise<LayoutSection>;
    updateSection(
      contentType: ContentType,
      id: string,
      data: UpdateSectionInput,
    ): Promise<LayoutSection>;
    deleteSection(contentType: ContentType, id: string): Promise<void>;
    reorderSections(
      contentType: ContentType,
      orders: ReorderItem[],
    ): Promise<void>;

    // ── Categories ─────────────────────────────────────────────────────────
    getCategories(contentType: ContentType): Promise<LayoutCategory[]>;
    createCategory(
      contentType: ContentType,
      data: CreateCategoryInput,
    ): Promise<LayoutCategory>;
    updateCategory(
      contentType: ContentType,
      id: string,
      data: UpdateCategoryInput,
    ): Promise<LayoutCategory>;
    deleteCategory(contentType: ContentType, id: string): Promise<void>;
    reorderCategories(
      contentType: ContentType,
      orders: ReorderItem[],
    ): Promise<void>;

    // ── Items ──────────────────────────────────────────────────────────────
    getCategoryItems(
      contentType: ContentType,
      categoryId: string,
    ): Promise<LayoutItem[]>;
    addItem(
      contentType: ContentType,
      categoryId: string,
      entityRef: string,
    ): Promise<LayoutItem>;
    removeItem(
      contentType: ContentType,
      categoryId: string,
      itemId: string,
    ): Promise<void>;
    reorderItems(
      contentType: ContentType,
      categoryId: string,
      orders: ReorderItem[],
    ): Promise<void>;
  };
  // ── Banners ───────────────────────────────────────────────────────────────
  banners: {
    getActiveBanners(): Promise<Banner[]>;
    getAllBanners(): Promise<Banner[]>;
    createBanner(data: CreateBannerInput): Promise<Banner>;
    updateBanner(id: string, data: UpdateBannerInput): Promise<Banner>;
    deleteBanner(id: string): Promise<void>;
    enableBanner(id: string): Promise<Banner>;
    disableBanner(id: string): Promise<Banner>;
  };
  // ── Toolchain Approvals ───────────────────────────────────────────────────
  toolchainApprovals: {
    /** Read approvals from the DB (populated in real-time by the harnessTriggerAction). */
    getPendingApprovals(): Promise<{ data: ToolchainApproval[] }>;
    /** Batch lookup of Harness execution status by Backstage task IDs (one DB query, indexed by PK). */
    getExecutionStatus(taskIds: string[]): Promise<{ data: Record<string, HarnessExecutionStatus> }>;
    /**
     * Returns pipeline output variables stored by the reconciler when it brought a crashed
     * execution to terminal success. Returns null if the task was not crash-recovered or has
     * no stored outputs. PipelineUrl is always included if present in our DB.
     */
    getTaskOutputs(taskId: string): Promise<{ data: Record<string, string> | null }>;
  };
  // ── Add new module namespaces below ──────────────────────────────────────
  faq: {
    // ── Owners ─────────────────────────────────────────────────────────────────
    getSections(): Promise<FaqSection[]>;
    createSection(data: CreateFaqSectionInput): Promise<FaqSection>;
    updateSection(id: string, data: UpdateFaqSectionInput): Promise<FaqSection>;
    deleteSection(id: string): Promise<void>;
    reorderSections(items: ReorderFaqItem[]): Promise<void>;
    // ── Categories ────────────────────────────────────────────────────────
    getCategories(sectionId: string): Promise<FaqCategory[]>;
    createCategory(sectionId: string, data: CreateFaqCategoryInput): Promise<FaqCategory>;
    updateCategory(sectionId: string, id: string, data: UpdateFaqCategoryInput): Promise<FaqCategory>;
    deleteCategory(sectionId: string, id: string): Promise<void>;
    reorderCategories(sectionId: string, items: ReorderFaqItem[]): Promise<void>;
    // ── Entries ──────────────────────────────────────────────────────────────
    getPublishedEntries(): Promise<FaqEntry[]>;
    searchEntries(q: string): Promise<FaqEntry[]>;
    getAllEntries(): Promise<FaqEntry[]>;
    createEntry(categoryId: string, data: CreateFaqEntryInput): Promise<FaqEntry>;
    updateEntry(id: string, data: UpdateFaqEntryInput): Promise<FaqEntry>;
    deleteEntry(id: string): Promise<void>;
    getFeedback(entryId: string): Promise<FeedbackSummary>;
    submitFeedback(entryId: string, helpful: boolean): Promise<FeedbackSummary>;
    getFeedbackVoters(entryId: string): Promise<FeedbackVoters>;
    getEntriesBySection(sectionId: string): Promise<FaqEntry[]>;
    getEntriesByCategory(categoryId: string): Promise<FaqEntry[]>;
    /** Admin: all statuses, full answer text, scoped to one category. */
    getAdminEntriesByCategory(categoryId: string): Promise<FaqEntry[]>;
    getRecentEntries(limit?: number): Promise<FaqEntry[]>;
    getEntryBySlug(slug: string): Promise<FaqEntry>;
  };
  // featureFlags: { ... };
  // ─────────────────────────────────────────────────────────────────────────
}
